import { useState, useEffect } from 'react';
import { expenseCategoryApi } from '../lib/api';
import {
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  Settings,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';

// Liste d'emojis suggérés pour les catégories
const SUGGESTED_ICONS = ['📁', '🍽️', '⚡', '💧', '🏥', '📋', '🚗', '📚', '🛠️', '💰', '🏠', '📱', '👔', '🎓', '🔧', '📦', '🧹', '🔌', '🚿', '💊'];

// Couleurs suggérées
const SUGGESTED_COLORS = [
  '#ff9800', '#ffeb3b', '#2196f3', '#f44336', '#9c27b0', 
  '#607d8b', '#4caf50', '#00bcd4', '#e91e63', '#795548',
  '#3f51b5', '#009688', '#ff5722', '#673ab7', '#8bc34a'
];

export default function ExpenseCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const [newCategory, setNewCategory] = useState({
    value: '',
    label: '',
    icon: '📁',
    color: '#607d8b',
  });

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await expenseCategoryApi.getAllIncludingInactive();
      setCategories(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const generateSlug = (label) => {
    return label
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  };

  const handleLabelChange = (label) => {
    setNewCategory({
      ...newCategory,
      label,
      value: editingCategory ? newCategory.value : generateSlug(label),
    });
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.label || !newCategory.value) {
      toast.error('Veuillez remplir le nom et l\'identifiant');
      return;
    }

    setFormLoading(true);
    try {
      if (editingCategory) {
        await expenseCategoryApi.update(editingCategory.id, newCategory);
        toast.success('Catégorie mise à jour');
      } else {
        await expenseCategoryApi.create(newCategory);
        toast.success('Catégorie créée');
      }
      setShowAddDialog(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await expenseCategoryApi.delete(id);
      toast.success('Catégorie supprimée');
      fetchCategories();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setNewCategory({
      value: category.value,
      label: category.label,
      icon: category.icon,
      color: category.color,
    });
    setShowAddDialog(true);
  };

  const handleSeedDefaults = async () => {
    try {
      await expenseCategoryApi.seedDefaults();
      toast.success('Catégories par défaut initialisées');
      fetchCategories();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetForm = () => {
    setEditingCategory(null);
    setNewCategory({
      value: '',
      label: '',
      icon: '📁',
      color: '#607d8b',
    });
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Catégories de Dépenses
          </h1>
          <p className="text-muted-foreground">Gérez les catégories pour organiser vos dépenses</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSeedDefaults}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Réinitialiser défauts
          </Button>
          <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-category-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle catégorie
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddCategory} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom de la catégorie *</Label>
                  <Input
                    value={newCategory.label}
                    onChange={(e) => handleLabelChange(e.target.value)}
                    placeholder="Ex: Transport, Fournitures..."
                    data-testid="category-label"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Identifiant (slug) *</Label>
                  <Input
                    value={newCategory.value}
                    onChange={(e) => setNewCategory({ ...newCategory, value: e.target.value })}
                    placeholder="Ex: transport"
                    disabled={!!editingCategory}
                    className={editingCategory ? 'bg-muted' : ''}
                    data-testid="category-value"
                  />
                  <p className="text-xs text-muted-foreground">Généré automatiquement, utilisé en interne</p>
                </div>

                <div className="space-y-2">
                  <Label>Icône (emoji)</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      value={newCategory.icon}
                      onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
                      className="w-20 text-center text-2xl"
                      data-testid="category-icon"
                    />
                    <div className="flex flex-wrap gap-1">
                      {SUGGESTED_ICONS.map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          onClick={() => setNewCategory({ ...newCategory, icon })}
                          className={`w-8 h-8 rounded hover:bg-muted flex items-center justify-center ${newCategory.icon === icon ? 'bg-primary/20 ring-2 ring-primary' : ''}`}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Couleur</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      type="color"
                      value={newCategory.color}
                      onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                      className="w-16 h-10 p-1 cursor-pointer"
                      data-testid="category-color"
                    />
                    <div className="flex flex-wrap gap-1">
                      {SUGGESTED_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setNewCategory({ ...newCategory, color })}
                          className={`w-6 h-6 rounded-full border-2 ${newCategory.color === color ? 'border-foreground' : 'border-transparent'}`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Aperçu */}
                <div className="p-4 bg-muted rounded-lg">
                  <Label className="text-xs text-muted-foreground mb-2 block">Aperçu</Label>
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                      style={{ backgroundColor: newCategory.color + '20', color: newCategory.color }}
                    >
                      {newCategory.icon}
                    </div>
                    <div>
                      <p className="font-medium">{newCategory.label || 'Nom de la catégorie'}</p>
                      <p className="text-xs text-muted-foreground">{newCategory.value || 'identifiant'}</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }} className="flex-1">
                    Annuler
                  </Button>
                  <Button type="submit" disabled={formLoading} className="flex-1">
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingCategory ? 'Modifier' : 'Créer')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des catégories ({categories.length})</CardTitle>
          <CardDescription>Cliquez sur une catégorie pour la modifier</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">Aucune catégorie créée</p>
              <Button onClick={handleSeedDefaults}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Initialiser les catégories par défaut
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Icône</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Identifiant</TableHead>
                  <TableHead>Couleur</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((category) => (
                  <TableRow 
                    key={category.id || category.value} 
                    className={`cursor-pointer hover:bg-muted/50 ${!category.is_active ? 'opacity-50' : ''}`}
                    onClick={() => category.id && handleEdit(category)}
                  >
                    <TableCell>
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                        style={{ backgroundColor: category.color + '20' }}
                      >
                        {category.icon}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{category.label}</TableCell>
                    <TableCell className="text-muted-foreground font-mono text-sm">{category.value}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-6 h-6 rounded-full border"
                          style={{ backgroundColor: category.color }}
                        />
                        <span className="text-xs text-muted-foreground font-mono">{category.color}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {category.is_active !== false ? (
                        <span className="text-green-600 text-sm">Actif</span>
                      ) : (
                        <span className="text-red-600 text-sm">Inactif</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-center gap-1">
                        {category.id && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(category)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(category.id)} className="text-red-600 hover:text-red-700">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
